import { useEffect, useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, Loader2, ArrowRight, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import TileSphereLogo from "@/components/TileSphereLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function SubscribeSuccess() {
  const [params] = useSearchParams();
  const { portal } = useParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling"); // polling | paid | active | error
  const [sub, setSub] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let stopped = false;
    let attempts = 0;
    const poll = async () => {
      while (!stopped && attempts < 30) {
        attempts++;
        try {
          const { data } = await axios.get(`${BACKEND}/api/platform/public/subscriptions/status/${sessionId}`);
          if (data.payment_status === "paid" && data.subscription_id) {
            // fetch full subscription state
            const { data: s } = await axios.get(`${BACKEND}/api/platform/public/subscriptions/${data.subscription_id}`);
            setSub(s);
            setStatus(s.status === "ACTIVE" ? "active" : "paid");
            if (s.status === "ACTIVE") return;
          }
        } catch (e) { /* keep polling */ }
        await new Promise((r) => setTimeout(r, 2500));
      }
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white relative overflow-hidden flex flex-col" data-testid="subscribe-success">
      <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-orange-500/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[700px] h-[700px] rounded-full bg-fuchsia-600/15 blur-3xl pointer-events-none" />

      <header className="relative z-10 px-6 py-5 max-w-7xl mx-auto w-full"><TileSphereLogo /></header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 py-10">
        <div className="max-w-xl w-full rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-10 shadow-2xl shadow-black/40 text-center">
          {status === "polling" ? (
            <>
              <Loader2 className="w-12 h-12 mx-auto animate-spin text-orange-400" />
              <h1 className="font-display text-2xl sm:text-3xl font-black mt-5">Confirming your payment…</h1>
              <p className="text-white/60 mt-2">Hold tight — Stripe is verifying the transaction.</p>
            </>
          ) : null}

          {status === "paid" ? (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-amber-500/20 border border-amber-500/40 flex items-center justify-center">
                <Clock className="w-6 h-6 text-amber-300" />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black mt-5">Payment received</h1>
              <p className="text-white/60 mt-2">Your subscription is now <span className="text-amber-300 font-semibold">PENDING APPROVAL</span> from the TileSphere team.</p>
              <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-4 text-left">
                <div className="text-xs uppercase tracking-[0.25em] text-orange-300 font-bold">Your subscription</div>
                <div className="mt-2 text-sm text-white/80">{sub?.company_name} · <span className="font-mono">{sub?.plan_code}</span> · {sub?.billing_cycle?.toLowerCase()}</div>
                <div className="text-2xl font-black mt-1">₹{(sub?.total_price || 0).toLocaleString()}</div>
              </div>
              <p className="text-xs text-white/40 mt-5">Once approved, we&apos;ll send your contract PDF via in-app notification &amp; this page will update automatically.</p>
            </>
          ) : null}

          {status === "active" ? (
            <>
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-300" />
              </div>
              <h1 className="font-display text-2xl sm:text-3xl font-black mt-5">You&apos;re live on TileSphere!</h1>
              <p className="text-white/60 mt-2">Your subscription is <span className="text-emerald-300 font-semibold">ACTIVE</span>. Your contract is ready to download.</p>
              <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-4 text-left">
                <div className="text-xs uppercase tracking-[0.25em] text-orange-300 font-bold">Subscription</div>
                <div className="mt-2 text-sm text-white/80">{sub?.company_name} · <span className="font-mono">{sub?.plan_code}</span></div>
                <div className="text-2xl font-black mt-1">₹{(sub?.total_price || 0).toLocaleString()}</div>
              </div>
              {sub?.contract?.id ? (
                <a
                  href={`${BACKEND}/api/contracts/${sub.contract.id}/download`}
                  className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold"
                  data-testid="download-contract"
                >
                  <Download className="w-4 h-4" /> Download contract PDF
                </a>
              ) : null}
              <div className="mt-6">
                <Link to="/login"><Button variant="outline" className="bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="goto-login">Go to login <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
              </div>
            </>
          ) : null}

          {status === "error" ? (
            <>
              <h1 className="font-display text-2xl sm:text-3xl font-black">Hmm, something went wrong.</h1>
              <p className="text-white/60 mt-2">We couldn&apos;t verify your payment session. If you were charged, contact <a className="text-orange-300 underline" href="mailto:support@projexino.com">support@projexino.com</a>.</p>
            </>
          ) : null}
        </div>
      </main>

      <footer className="relative z-10 border-t border-white/10 py-6 px-6 text-white/40 text-xs text-center">
        © 2026 TileSphere · An ecosystem by Projexino
      </footer>
    </div>
  );
}
